import { getLastFillup, getNewPortalUsers } from 'lib/firebase';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import styles from './CreateDeliveryModal.module.scss';
import * as yup from 'yup';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { FormModal } from 'components/form/FormModal/FormModal';
import { FieldValues } from 'react-hook-form';
import { constructTicket, normalizeDeliveryTicket } from 'helpers/planning';
import { CreateDeliveryForm } from './CreateDeliveryForm';
import { FormSelectOption } from 'components/form/types';
import {
    useLazyCreateDeliveryTicketQuery,
    useLazyCreatePreResolvedTicketQuery,
} from 'store/services/planning';
import { DeviceLookup } from 'views/Planning/Planning';
import { useSnackbar } from 'hooks/useSnackbar';

type DeliveryTicketInput =
    | Omit<Partial<Ticket>, 'taggedUsers | device'>
    | {
          taggedUsers?: string;
          device?: string;
      };

const deliverySchema = yup.object({
    device: yup.string().required(),
    taggedUsers: yup.string().nullable(),
    plannedDate: yup.date().nullable(),
    plannedGallons: yup.number().nullable(),
    delivery_instructions: yup.string().nullable(),
});

interface CreateDeliveryModalProps extends DefaultOpenModalProps {
    deliveries: Array<Ticket>;
    devicesList: DeviceLookup;
    setDevicesList: React.Dispatch<React.SetStateAction<DeviceLookup>>;
    setAllDeliveries: (
        d: Ticket[] | ((deliveries: Ticket[]) => Ticket[])
    ) => void;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier: string;
}

const defaultValues: DeliveryTicketInput = {
    device: '',
    taggedUsers: '',
    delivery_instructions: '',
    plannedDate: new Date(),
    plannedGallons: 0,
};

export const CreateDeliveryModal = ({
    modalOpen,
    setModalOpen,
    setAllDeliveries,
    devicesList,
    setDevicesList,
    viewAsSupplier,
}: CreateDeliveryModalProps) => {
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );

    const [userOptions, setUserOptions] = useState<FormSelectOption[]>([]);
    const [isPreResolveChecked, setIsPreResolveChecked] = useState(false);

    const activateSnackbar = useSnackbar();

    const onClose = () => {
        setModalOpen(false);
    };

    useEffect(() => {
        if (!!authentication.supplierId || !!viewAsSupplier) {
            getNewPortalUsers(viewAsSupplier ?? authentication.supplierId).then(
                (newPortalUsers) => {
                    if (newPortalUsers) {
                        const userOpts = Object.keys(newPortalUsers).map(
                            (key: string) => {
                                return {
                                    name: newPortalUsers[key]?.email || '',
                                    value: newPortalUsers[key]?.uid || '',
                                };
                            }
                        );

                        setUserOptions(userOpts);
                    }
                }
            );
        }
    }, [authentication, viewAsSupplier]);

    const [createDeliveryQuery] = useLazyCreateDeliveryTicketQuery();
    const [createResolvedDeliveryQuery] = useLazyCreatePreResolvedTicketQuery();

    const onSubmit = (data: FieldValues) => {
        const {
            plannedDate,
            plannedGallons,
            device,
            taggedUsers,
            delivery_instructions,
        } = data;

        if (
            !authentication.supplierId ||
            authentication.supplierId === null ||
            !device
        ) {
            return;
        }

        const selectedDevice = device;

        const selectedDriver = taggedUsers
            ? authentication.newPortalUsers?.[taggedUsers]
            : null;

        if (!selectedDevice) {
            activateSnackbar({
                message: 'Device required.',
                severity: 'error',
            });
        }

        getLastFillup(device).then((result) => {
            if (authentication.supplierId) {
                const newTicket = constructTicket({
                    createdBy: authentication.user?.uid,
                    plannedGallons,
                    supplierId: viewAsSupplier ?? authentication.supplierId,
                    fillup: result,
                    delivery_instructions,
                    deviceId: selectedDevice,
                    plannedDate,
                    selectedDriver,
                    name: devicesList[selectedDevice]?.name ?? '',
                });

                if (isPreResolveChecked) {
                    newTicket.dateDelivered = plannedDate;
                    newTicket.gallonsDelivered = plannedGallons;
                    newTicket.status = 'done';
                }

                const query = isPreResolveChecked
                    ? createResolvedDeliveryQuery
                    : createDeliveryQuery;

                query({
                    supplierId: viewAsSupplier ?? authentication.supplierId,
                    deviceId: selectedDevice,
                    ticket: newTicket,
                }).then((ticketResult) => {
                    if (ticketResult.data) {
                        setAllDeliveries((oldDeliveries: Ticket[]) => {
                            if (ticketResult.data) {
                                activateSnackbar({
                                    message: 'Ticket created successfully',
                                    severity: 'success',
                                });
                                return [
                                    normalizeDeliveryTicket(ticketResult.data),
                                    ...oldDeliveries,
                                ];
                            }

                            return oldDeliveries;
                        });

                        onClose();
                    }
                });
            }
        });
    };

    return (
        <div data-testid="CreateDeliveryModal" className={styles.container}>
            <FormModal
                defaultValues={defaultValues}
                validationSchema={deliverySchema}
                onValidHandler={onSubmit}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                title="Create Delivery Ticket"
            >
                <CreateDeliveryForm
                    userOptions={userOptions}
                    user={authentication.user}
                    viewAsSupplier={viewAsSupplier}
                    devicesList={devicesList}
                    isPreResolveChecked={isPreResolveChecked}
                    setIsPreResolveChecked={setIsPreResolveChecked}
                    setDevicesList={setDevicesList}
                />
            </FormModal>
        </div>
    );
};
